import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Eye } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { downloadFile } from "@/lib/api/download";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { RowActions } from "@/components/ui/RowActions";
import { Modal } from "@/components/ui/Modal";

/**
 * Báo cáo XH nhân viên (empRating.zul) — lưới 18 cột xếp hạng nhân viên theo kỳ.
 * Nguồn là bảng `EMP_RATING`; cả 2 hệ hiện đều 0 bản ghi.
 */
type Row = {
  EMP_RATING_ID: number;
  ORG_NAME: string | null;
  RATING_PERIOD: string | null;
  PERFORMER_NAME: string | null;
  ASSESSOR_NAME: string | null;
  AVR_POINT_STAFF: number | null;
  KI_NAME_STAFF: string | null;
  AVR_POINT: number | null;
  KI_NAME: string | null;
  STATUS: number | null;
  SOURCE_TYPE: number | null;
  EMPLOYEE_CODE: string | null;
  FULL_NAME: string | null;
  POSITION_NAME: string | null;
  RANK: number | null;
  RANK_NAME: string | null;
  COMMENT_LEADER: string | null;
  SEND_RATING_DATE: string | null;
  APPROVE_RATING_DATE: string | null;
};

type Filters = {
  fromPeriod: string;
  toPeriod: string;
  employeeCode: string;
  fullName: string;
  status: string;
  source: string;
};
const EMPTY: Filters = {
  fromPeriod: "",
  toPeriod: "",
  employeeCode: "",
  fullName: "",
  status: "",
  source: "",
};

const STATUS_LABEL: Record<number, string> = { 1: "Chờ đánh giá", 2: "Đã đánh giá", 3: "Đã chốt" };
/** `EMP_RATING.TYPE` — hệ cũ gọi là "Nguồn đánh giá" (AppConstants.EMP_RATING.SOURCE). */
const SOURCE_LABEL: Record<number, string> = { 0: "eOffice", 1: "Excel" };
const day = (v: string | null) => (v ? v.slice(0, 10) : "—");
const txt = (v: string | null) => v ?? "—";
const num = (v: number | null) => (v == null ? "—" : String(v));

export function StaffRatingReportPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [detail, setDetail] = useState<Row | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["emp-rating-report", page, pageSize, applied],
    queryFn: async () => {
      const { data } = await api.get("/task/rating/report", {
        params: {
          page,
          pageSize: pageSize,
          fromPeriod: applied.fromPeriod || undefined,
          toPeriod: applied.toPeriod || undefined,
          employeeCode: applied.employeeCode || undefined,
          fullName: applied.fullName || undefined,
          status: applied.status || undefined,
          source: applied.source || undefined,
        },
      });
      return (data.data ?? data) as { items: Row[]; total: number };
    },
  });

  const columns: Column<Row>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "action",
      header: "Thao tác",
      width: "90px",
      align: "center",
      render: (r) => (
        <RowActions
          actions={[
            {
              label: "Xem chi tiết",
              icon: Eye,
              onClick: () => setDetail(r),
            },
          ]}
        />
      ),
    },
    { key: "org", header: "Phòng ban/ Đơn vị", width: "200px", render: (r) => txt(r.ORG_NAME) },
    { key: "period", header: "Kỳ đánh giá", width: "110px", render: (r) => txt(r.RATING_PERIOD) },
    {
      key: "performer",
      header: "Người thực hiện đánh giá",
      width: "190px",
      render: (r) => txt(r.PERFORMER_NAME),
    },
    {
      key: "assessor",
      header: "Người quản lý đánh giá",
      width: "190px",
      render: (r) => txt(r.ASSESSOR_NAME),
    },
    {
      key: "selfPoint",
      header: "Tự đánh giá",
      width: "120px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.AVR_POINT_STAFF)}</span>,
    },
    {
      key: "mgrPoint",
      header: "QL đánh giá",
      width: "120px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.AVR_POINT)}</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "130px",
      render: (r) => (r.STATUS == null ? "—" : (STATUS_LABEL[r.STATUS] ?? `#${r.STATUS}`)),
    },
    {
      key: "source",
      header: "Nguồn",
      width: "100px",
      render: (r) =>
        r.SOURCE_TYPE == null ? "—" : (SOURCE_LABEL[r.SOURCE_TYPE] ?? `#${r.SOURCE_TYPE}`),
    },
    { key: "code", header: "Mã nhân viên", width: "120px", render: (r) => txt(r.EMPLOYEE_CODE) },
    { key: "name", header: "Họ và tên", width: "180px", render: (r) => txt(r.FULL_NAME) },
    { key: "position", header: "Chức vụ", width: "160px", render: (r) => txt(r.POSITION_NAME) },
    {
      key: "rank",
      header: "Điểm XH",
      width: "100px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.RANK)}</span>,
    },
    { key: "rankName", header: "Kết quả XH", width: "130px", render: (r) => txt(r.RANK_NAME) },
    { key: "comment", header: "Ý kiến", width: "200px", render: (r) => txt(r.COMMENT_LEADER) },
    {
      key: "sendDate",
      header: "Ngày gửi đánh giá",
      width: "140px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.SEND_RATING_DATE)}</span>,
    },
    {
      key: "approveDate",
      header: "Ngày đánh giá",
      width: "130px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.APPROVE_RATING_DATE)}</span>,
    },
  ];

  /** Nút "Xuất báo cáo" — hệ cũ có ở mọi lưới báo cáo. */
  const onExport = () =>
    downloadFile(
      "/task/rating/report/export",
      {
        fromPeriod: applied.fromPeriod,
        toPeriod: applied.toPeriod,
        employeeCode: applied.employeeCode,
        fullName: applied.fullName,
        status: applied.status,
      },
      "bao-cao-xh-nhan-vien.csv",
    );

  return (
    <div>
      <PageHeader
        title="Báo cáo xã hội nhân viên"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} bản đánh giá`}
        breadcrumb="Báo cáo › XH nhân viên"
      />
      <SearchPanel
        fields={[
          {
            label: "Kỳ đánh giá từ tháng",
            control: (
              <input
                className={filterInputClass}
                placeholder="MM/yyyy"
                value={filters.fromPeriod}
                onChange={(e) => setFilters((f) => ({ ...f, fromPeriod: e.target.value }))}
              />
            ),
          },
          {
            label: "Đến tháng",
            control: (
              <input
                className={filterInputClass}
                placeholder="MM/yyyy"
                value={filters.toPeriod}
                onChange={(e) => setFilters((f) => ({ ...f, toPeriod: e.target.value }))}
              />
            ),
          },
          {
            label: "Mã NV",
            control: (
              <input
                className={filterInputClass}
                value={filters.employeeCode}
                onChange={(e) => setFilters((f) => ({ ...f, employeeCode: e.target.value }))}
              />
            ),
          },
          {
            label: "Họ và tên",
            control: (
              <input
                className={filterInputClass}
                value={filters.fullName}
                onChange={(e) => setFilters((f) => ({ ...f, fullName: e.target.value }))}
              />
            ),
          },
          {
            label: "Nguồn đánh giá",
            control: (
              <select
                className={filterInputClass}
                value={filters.source}
                onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                {Object.entries(SOURCE_LABEL).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: "Trạng thái",
            control: (
              <select
                className={filterInputClass}
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                {Object.entries(STATUS_LABEL).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            ),
          },
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
        onExport={onExport}
      />
      <div className="overflow-x-auto">
        <div className="min-w-[2400px]">
          <DataTable<Row>
            columns={columns}
            rows={data?.items ?? []}
            keyBy={(r) => r.EMP_RATING_ID}
            isLoading={isLoading}
            empty={
              <>
                <Users className="inline h-4 w-4 mr-1" /> Chưa có bản đánh giá nào — hệ cũ cũng đang
                trống (bảng <code>emp_rating</code> = 0 bản ghi).
              </>
            }
            dense
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Hệ cũ mở chi tiết bản đánh giá từ cột Thao tác. */}
      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={`Đánh giá ${detail?.FULL_NAME ?? ""} — kỳ ${detail?.RATING_PERIOD ?? ""}`}
      >
        {detail && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {(
              [
                ["Phòng ban/ Đơn vị", txt(detail.ORG_NAME)],
                ["Mã nhân viên", txt(detail.EMPLOYEE_CODE)],
                ["Chức vụ", txt(detail.POSITION_NAME)],
                ["Người thực hiện đánh giá", txt(detail.PERFORMER_NAME)],
                ["Người quản lý đánh giá", txt(detail.ASSESSOR_NAME)],
                ["Tự đánh giá", `${num(detail.AVR_POINT_STAFF)} ${txt(detail.KI_NAME_STAFF)}`],
                ["QL đánh giá", `${num(detail.AVR_POINT)} ${txt(detail.KI_NAME)}`],
                ["Điểm XH", num(detail.RANK)],
                ["Kết quả XH", txt(detail.RANK_NAME)],
                [
                  "Trạng thái",
                  detail.STATUS == null
                    ? "—"
                    : (STATUS_LABEL[detail.STATUS] ?? `#${detail.STATUS}`),
                ],
                [
                  "Nguồn",
                  detail.SOURCE_TYPE == null
                    ? "—"
                    : (SOURCE_LABEL[detail.SOURCE_TYPE] ?? `#${detail.SOURCE_TYPE}`),
                ],
                ["Ngày gửi đánh giá", day(detail.SEND_RATING_DATE)],
                ["Ngày đánh giá", day(detail.APPROVE_RATING_DATE)],
                ["Ý kiến", txt(detail.COMMENT_LEADER)],
              ] as [string, string][]
            ).map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Modal>
    </div>
  );
}
